import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import DivisionSelector from '../../common/inputs/DivisionSelector';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import DisplayText from '../../common/text/DisplayText';

import CompanySettingsCard from '../CompanySettingsCard';
import SettingsCardHeader from '../SettingsCardHeader';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import FormBuilder from '../../forms/FormBuilder/FormBuilder';

import {
  getCustomFields,
  setCustomFields,
} from '../../timecards/state/timecards.actions';
import { getCustomTables } from '../../forms/state/forms.actions';
import { getCompanySettings } from '../state/settings.actions';

function CustomFields({
  visible,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.settings.divisions);
  const customFields = useSelector((state) => state.timecards.customFields);

  const [fields, setFields] = useState([]);
  const [divisionId, setDivisionId] = useState();
  const [pendingClockInOutFlag, setPendingClockInOutFlag] = useState();

  const enableClockInClockOutCustomFields = useMemo(() => (
    !!customFields[divisionId]?.enableClockInClockOutCustomFields
  ), [customFields, divisionId]);

  useEffect(() => {
    setPendingClockInOutFlag(enableClockInClockOutCustomFields);
  }, [divisionId, enableClockInClockOutCustomFields, visible]);

  const onSaveFields = useCallback(async () => {
    const saved = await dispatch(setCustomFields({
      fields,
      divisionId,
      enableClockInClockOutCustomFields: pendingClockInOutFlag,
    }));
    if (saved) {
      await dispatch(getCompanySettings());
    }
  }, [fields, divisionId, pendingClockInOutFlag]);

  useEffect(() => {
    if (visible) {
      dispatch(getCustomFields());
      dispatch(getCustomTables());
    }
  }, [dispatch, visible]);

  useEffect(() => {
    if (!divisionId || !(divisionId in customFields)) return;
    const {
      [divisionId]: { fields: newFields = [] } = {},
    } = customFields;
    setFields(newFields);
  }, [customFields, divisionId]);

  useEffect(() => {
    const divisionIds = Object.keys(divisions);
    if (!divisionId && divisionIds.length > 0) {
      setDivisionId(divisionIds[0]);
    }
  }, [divisionId, divisions]);

  const initialFields = useMemo(() => {
    const {
      [divisionId]: { fields: sections = [] } = {},
    } = customFields;

    const responseSections = sections.filter((section) => section.id !== 'timecard');
    return [
      ...responseSections,
      {
        name: 'Time Card',
        id: 'timecard',
        hidden: true,
        settings: {},
        fields: [
          {
            configProps: {
              title: 'Total Hours',
              required: false,
            },
            id: 'total_hours',
            selectedType: 'text',
          },
          {
            configProps: {
              title: 'Work Hours',
              required: false,
            },
            id: 'work_hours',
            selectedType: 'text',
          },
          {
            configProps: {
              title: 'Break Hours',
              required: false,
            },
            id: 'break_hours',
            selectedType: 'text',
          },
          {
            configProps: {
              title: t('Project'),
              required: false,
              dataType: 'Projects',
              optional: true,
            },
            id: 'project',
            selectedType: 'dropdown',
          },
          {
            configProps: {
              title: 'Cost Code',
              required: false,
              dataType: 'Costcodes',
              optional: true,
            },
            id: 'costcode',
            selectedType: 'dropdown',
          },
          {
            configProps: {
              title: 'Cost Code - Code',
              required: false,
            },
            id: 'costcode_code',
            selectedType: 'text',
          },
          {
            configProps: {
              title: 'Cost Code - Name',
              required: false,
            },
            id: 'costcode_name',
            selectedType: 'text',
          },
          {
            configProps: {
              title: 'User',
              required: false,
              dataType: 'Users',
              optional: true,
            },
            id: 'user',
            selectedType: 'dropdown',
          },
        ],
      },
    ];
  }, [customFields, divisionId]);

  const title = useMemo(() => {
    const divisionList = Object.keys(divisions);
    if (divisionList.length <= 1) return 'Custom Time Card Fields';
    return (
      <DivisionSelector
        divisionId={divisionId}
        onChange={setDivisionId}
        allowClear={false}
        style={{ width: 250 }}
      />
    );
  }, [divisions, divisionId]);

  const clockInOutSwitchHelp = enableClockInClockOutCustomFields
    ? null
    : 'When enabled, employees in this division fill time card custom fields when clocking in and out on mobile. Note auto clock out is disabled company-wide while any division has this enabled.';

  return (
    <Row
      id="divisions-container"
      style={{
        width: '100%',
        height: '95%',
        marginLeft: 10,
        marginTop: 10,
        marginBottom: 20,
        position: 'relative',
      }}
    >
      <SettingsCardHeader
        title={title}
        style={{ width: '100%', paddingRight: 50 }}
      >
        <Row align="middle" gutter={16} wrap={false}>
          <Tooltip title={clockInOutSwitchHelp}>
            <Row align="middle" style={{ flexWrap: 'nowrap' }}>
              <DisplayText
                title="Clock In / Clock Out Custom Fields"
                style={{
                  fontFamily: 'roboto-regular',
                  fontSize: 13,
                  marginBottom: 0,
                  marginRight: 8,
                  whiteSpace: 'nowrap',
                }}
              />
              <OnTraccrSwitch
                value={pendingClockInOutFlag}
                onChange={setPendingClockInOutFlag}
              />
            </Row>
          </Tooltip>
          <OnTraccrButton
            title="Save"
            type="primary"
            onClick={onSaveFields}
          />
        </Row>
      </SettingsCardHeader>
      <CompanySettingsCard
        title=""
        style={{
          position: 'absolute',
          top: 30,
          bottom: 20,
          right: 50,
          left: 0,
          padding: 0,
          height: 'auto',
        }}
        containerStyle={{
          paddingLeft: 5,
          paddingRight: 5,
          width: '100%',
          border: 'none',
          height: 'inherit',
          maxHeight: 'none',
          overflowY: 'visible',
        }}
      >
        <div
          className="form-builder-container"
          style={{
            width: '100%', height: '100%', position: 'relative',
          }}
        >
          <FormBuilder
            key={divisionId}
            style={{ inset: 0 }}
            isBoardCards
            onSectionsChanged={setFields}
            initialSections={initialFields}
            divisions={[divisionId]}
            isTimeCard
            canAddDynamicAttribute
            showParentAttributeCheckbox
            enablePermissions
            allowRepeatingSections={false}
          />
        </div>
      </CompanySettingsCard>
    </Row>
  );
}

CustomFields.propTypes = {
  visible: PropTypes.bool.isRequired,
};

export default CustomFields;
